import React from 'react';
import { Check, Crown, Rocket, Star, Lock } from 'lucide-react';
import { Button } from './ui/button';

const tierStyles = {
  ascend: {
    border: 'border-blue-500/50',
    bg: 'bg-slate-900/50',
    text: 'text-blue-100',
    accent: 'text-blue-300',
    icon: Star,
    buttonClass: 'bg-blue-600 hover:bg-blue-700'
  },
  growth: {
    border: 'border-purple-500/50',
    bg: 'bg-slate-800/50',
    text: 'text-purple-100',
    accent: 'text-purple-300',
    icon: Rocket,
    buttonClass: 'bg-purple-600 hover:bg-purple-700',
    popular: true
  },
  founder: {
    border: 'border-amber-500/50',
    bg: 'bg-gradient-to-br from-slate-900 to-slate-800',
    text: 'text-amber-100',
    accent: 'text-amber-400',
    glow: 'gold-glow',
    icon: Crown,
    buttonClass: 'bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700'
  }
};

export const MembershipCard = ({ 
  tier, 
  name, 
  price, 
  features, 
  onSelect, 
  isCurrentTier, 
  disabled,
  initiationFee = 0,
  seatsRemaining = null,
  isOpen = true
}) => {
  const style = tierStyles[tier] || tierStyles.ascend;
  const Icon = style.icon;
  const isFounderLocked = tier === 'founder' && (!isOpen || seatsRemaining === 0);
  
  return (
    <div 
      className={`relative p-6 rounded-lg border ${style.border} ${style.bg} ${style.glow || ''} card-shadow transition-all hover:scale-105 ${isFounderLocked ? 'opacity-75' : ''}`}
      data-testid={`membership-card-${tier}`}
    >
      {/* Popular Badge */}
      {style.popular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
            Most Popular
          </span>
        </div>
      )}
      
      {/* Founder Seats Badge */}
      {tier === 'founder' && seatsRemaining !== null && (
        <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 ${isFounderLocked ? 'bg-red-500' : 'bg-amber-500'} text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1`}>
          {isFounderLocked ? (
            <>
              <Lock className="w-3 h-3" /> Sold Out
            </>
          ) : (
            <>{seatsRemaining} Seats Left</>
          )}
        </div>
      )}
      
      <div className="mb-4 mt-2">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            tier === 'founder' ? 'bg-amber-500/20' : 
            tier === 'growth' ? 'bg-purple-500/20' : 'bg-blue-500/20'
          }`}>
            <Icon className={`w-5 h-5 ${
              tier === 'founder' ? 'text-amber-500' : 
              tier === 'growth' ? 'text-purple-500' : 'text-blue-500'
            }`} />
          </div>
          <h3 className={`text-2xl font-playfair font-bold ${style.text} tracking-tight`}>{name}</h3>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-primary">${price}</span>
          <span className="text-muted-foreground">/month</span>
        </div>
        {initiationFee > 0 && (
          <p className="text-sm text-amber-400 mt-1">
            + ${initiationFee} one-time initiation
          </p>
        )}
      </div>
      
      <ul className="space-y-3 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              tier === 'founder' ? 'text-amber-500' : 
              tier === 'growth' ? 'text-purple-500' : 'text-blue-500'
            }`} />
            <span className={`text-sm ${style.accent}`}>{feature}</span>
          </li>
        ))}
      </ul>
      
      <Button
        onClick={onSelect}
        disabled={disabled || isCurrentTier || isFounderLocked}
        className={`w-full text-white font-semibold ${style.buttonClass}`}
        data-testid={`select-${tier}-btn`}
      >
        {isCurrentTier ? 'Current Plan' : isFounderLocked ? 'Unavailable' : disabled ? 'Processing...' : 'Select Plan'}
      </Button>
    </div>
  );
};
